import { useState } from "react";
import type { AimfoxCampaign } from "../../lib/types";
import { ALL_CAMPAIGNS } from "../../lib/types";
import { Button } from "../ui/button";

interface CampaignOption {
  id: string;
  name: string;
}

interface CampaignMultiSelectProps {
  /** Every ACTIVE campaign — the dropdown never lists a paused/finished one
   * (per spec: "we will show only active campaigns"), "All campaigns" is
   * added as the last option regardless of live campaign state. */
  activeCampaigns: AimfoxCampaign[];
  /** campaignIds already added to this recipient — excluded from the list so
   * the same campaign can't be selected twice; remove it first to re-add
   * with different message types. */
  alreadySelected: string[];
  onAdd: (campaignId: string) => void;
  /** The picker's own fetch (GET /api/v2/campaigns-picker) hasn't resolved
   * yet — shown instead of "every campaign has already been added" so an
   * empty `activeCampaigns` doesn't read as "there are no campaigns" while
   * it's actually still loading. Should typically resolve well before the
   * operator reaches this control, since it's kicked off the moment the
   * Settings dialog opens, independent of the (fast) recipients list. */
  loading?: boolean;
}

// Reusable "pick a campaign to add" control — a select plus an Add button
// rather than a native multi-select, since each addition also needs its own
// message-type row underneath (see CampaignPrefRow) that a plain <select
// multiple> can't drive one at a time.
export function CampaignMultiSelect({ activeCampaigns, alreadySelected, onAdd, loading }: CampaignMultiSelectProps) {
  const options: CampaignOption[] = [
    ...(alreadySelected.includes(ALL_CAMPAIGNS) ? [] : [{ id: ALL_CAMPAIGNS, name: "All campaigns" }]),
    ...activeCampaigns
      .filter((c) => !alreadySelected.includes(c.id))
      .map((c) => ({ id: c.id, name: c.name })),
  ];
  const [pending, setPending] = useState<string>(options[0]?.id ?? "");

  // The select's value can point at a campaign that just got added by a
  // sibling row re-rendering with a new `options` list — keep the pending
  // choice valid rather than silently submitting whatever used to be there.
  const value = options.some((o) => o.id === pending) ? pending : options[0]?.id ?? "";

  if (loading && activeCampaigns.length === 0) {
    return (
      <p className="font-body-sm text-body-sm text-on-surface-variant">Loading campaigns…</p>
    );
  }

  if (options.length === 0) {
    return (
      <p className="font-body-sm text-body-sm text-on-surface-variant">
        Every active campaign (and All campaigns) has already been added.
      </p>
    );
  }

  return (
    <div className="flex items-center gap-spacing-xs">
      <select
        value={value}
        onChange={(e) => setPending(e.target.value)}
        className="h-8 flex-1 px-2 bg-surface-container-low text-on-surface font-body-sm text-body-sm rounded-xl outline-none border border-transparent focus:border-primary"
        aria-label="Campaign to add"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      <Button type="button" variant="secondary" onClick={() => onAdd(value)}>
        Add
      </Button>
    </div>
  );
}
